export type OperatorSymbol = "+" | "-" | "*" | "/";

export function performOperation(
  a: number,
  b: number,
  operator: OperatorSymbol
): number {
  switch (operator) {
    case "+":
      return a + b;
    case "-":
      return a - b;
    case "*":
      return a * b;
    case "/":
      return b === 0 ? NaN : a / b;
  }
}

export type CalculatorState = {
  displayValue: string;
  firstOperand: number | null;
  waitingForSecondOperand: boolean;
  operator: OperatorSymbol | null;
};

export const initialCalculatorState: CalculatorState = {
  displayValue: "0",
  firstOperand: null,
  waitingForSecondOperand: false,
  operator: null,
};

export function inputDigit(
  state: CalculatorState,
  digit: string
): CalculatorState {
  if (state.waitingForSecondOperand) {
    return { ...state, displayValue: digit, waitingForSecondOperand: false };
  }
  return {
    ...state,
    displayValue:
      state.displayValue === "0" ? digit : state.displayValue + digit,
  };
}

export function inputDecimal(state: CalculatorState): CalculatorState {
  if (state.waitingForSecondOperand) {
    return { ...state, displayValue: "0.", waitingForSecondOperand: false };
  }
  if (state.displayValue.includes(".")) return state;
  return { ...state, displayValue: state.displayValue + "." };
}

export function clearAll(): CalculatorState {
  return { ...initialCalculatorState };
}

export function applyOperator(
  state: CalculatorState,
  nextOperator: OperatorSymbol
): CalculatorState {
  const inputValue = parseFloat(state.displayValue);
  if (Number.isNaN(inputValue)) return state;

  if (state.operator && state.waitingForSecondOperand) {
    return { ...state, operator: nextOperator };
  }

  if (state.firstOperand === null) {
    return {
      ...state,
      firstOperand: inputValue,
      waitingForSecondOperand: true,
      operator: nextOperator,
    };
  }

  if (state.operator) {
    const result = performOperation(
      state.firstOperand,
      inputValue,
      state.operator
    );
    return {
      ...state,
      displayValue: String(result),
      firstOperand: result,
      waitingForSecondOperand: true,
      operator: nextOperator,
    };
  }

  return { ...state, waitingForSecondOperand: true, operator: nextOperator };
}

export function equals(state: CalculatorState): CalculatorState {
  if (
    state.operator === null ||
    state.waitingForSecondOperand ||
    state.firstOperand === null
  ) {
    return state;
  }
  const inputValue = parseFloat(state.displayValue);
  const result = performOperation(
    state.firstOperand,
    inputValue,
    state.operator
  );
  return {
    ...state,
    displayValue: String(result),
    firstOperand: result,
    operator: null,
    waitingForSecondOperand: true,
  };
}
